import { useEffect, useState } from 'react'
import { Link2 } from 'lucide-react'
import { ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { fmtNumber } from '@/shared/lib/format'
import { explain } from '@/shared/api/problem'
import type {
  ConcentrationResponse, EcosystemResponse, ProgramResponse,
} from '@/shared/api/types'
import { analyticsConcentration, analyticsEcosystem } from '../api'
import { Truncation } from '../parts'
import { NodeTypeBadge, edgeTypeLabel, nodeTypeLabel } from '../vocab'

/**
 * Connections — this programme's place in the ecosystem, as a list rather than
 * a picture.
 *
 * The graph itself lives on its own analytics screen. What belongs in a drawer
 * is the answerable question: who executes this, who funds it, where does it
 * operate, and is any of that concentrated in one person or one organisation.
 *
 * PollSphere's equivalent tab drew six concentric rings of `cos/sin` nodes that
 * overlapped at any realistic count, and re-emitted the same edge once per
 * candidate-programme pair.
 */
export function ConnectionsPanel({ program }: { program: ProgramResponse }) {
  const [eco, setEco] = useState<EcosystemResponse | null>(null)
  const [conc, setConc] = useState<ConcentrationResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    analyticsEcosystem({}).then((d) => { if (alive) setEco(d) })
      .catch((e) => { if (alive) setError(explain(e)) })
    analyticsConcentration({}).then((d) => { if (alive) setConc(d) }).catch(() => {})
    return () => { alive = false }
  }, [program.id])

  if (error) return <ErrorStrip message={error} />
  if (!eco) return <LoadingBlock label="Loading connections…" />

  // `nodeId` is an opaque String in the contract — the `TYPE:uuid` shape this
  // used to assume appears nowhere in it. When the guess missed, `mine` came
  // back empty and the panel stated as FACT that the programme had no partner,
  // no executing mobilizer and no recorded unit. `entityId` is the real join
  // and is on the DTO for exactly this.
  const meNode = eco.nodes.find(
    (n) => n.nodeTypeCode === 'PROGRAMME' && n.entityId === program.id,
  )
  const me = meNode?.nodeId
  const mine = me ? eco.edges.filter((e) => e.sourceId === me || e.targetId === me) : []
  const nodeOf = (id: string) => eco.nodes.find((n) => n.nodeId === id)

  return (
    <div className="space-y-4 px-1">
      <section className="space-y-2">
        <h4 className="text-erp-sm font-medium flex items-center gap-1.5">
          <Link2 className="h-3.5 w-3.5" /> Connected to this programme
        </h4>
        {mine.length === 0 ? (
          <p className="text-erp-xs text-muted-foreground">
            {meNode
              ? 'Nothing connected yet — no partner, no executing mobilizer, no recorded unit.'
              : 'This programme is not in the ecosystem graph for your scope, so its connections cannot be listed. That is not the same as having none.'}
          </p>
        ) : (
          <ul className="space-y-1">
            {mine.map((e, i) => {
              const otherId = e.sourceId === me ? e.targetId : e.sourceId
              const other = nodeOf(otherId)
              return (
                <li key={`${e.sourceId}-${e.targetId}-${e.edgeTypeCode}-${i}`}
                  className="flex flex-wrap items-center gap-2 text-erp-xs">
                  {other && <NodeTypeBadge code={other.nodeTypeCode} />}
                  <span className="font-medium">{other?.label ?? otherId}</span>
                  <span className="text-muted-foreground">{edgeTypeLabel(e.edgeTypeCode)}</span>
                  {e.weight > 1 && (
                    <span className="text-muted-foreground">×{fmtNumber(e.weight)}</span>
                  )}
                </li>
              )
            })}
          </ul>
        )}
        <Truncation truncatedTo={eco.nodes.length} totalAvailable={eco.nodes.length + eco.truncatedNodes}
          noun="nodes" />
      </section>

      <section className="space-y-2">
        <h4 className="text-erp-sm font-medium">Concentration risk</h4>
        <p className="text-erp-xs text-muted-foreground">
          One person or one organisation carrying many programmes is a single point of failure,
          and it is the question the ecosystem view exists to answer.
        </p>
        {!conc || conc.rows.length === 0 ? (
          <p className="text-erp-xs text-muted-foreground">Nothing concentrated enough to flag.</p>
        ) : (
          <ul className="space-y-1">
            {conc.rows.map((r, i) => (
              <li key={`${r.nodeTypeCode}-${r.entityId ?? r.label}-${i}`}
                className="flex flex-wrap items-center gap-2 text-erp-xs">
                <NodeTypeBadge code={r.nodeTypeCode} />
                <span className="font-medium">{r.label}</span>
                <span className="text-muted-foreground">
                  across {fmtNumber(r.programmeCount)} programmes
                </span>
                {/* Partner rows are matched by NAME, because there is no partner
                    master record in Axis. Saying so beats implying an id match. */}
                {r.nodeTypeCode === 'PARTNER' && (
                  <span className="text-muted-foreground italic">matched by name</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <p className="text-erp-xs text-muted-foreground">
        {nodeTypeLabel('CANDIDATE')} and {nodeTypeLabel('PARTY')} connections arrive with the
        political-landscape phase; nothing is shown for them until they exist.
      </p>
    </div>
  )
}
